import { Component } from '@theme/component';

/**
 * Live countdown for the preorder status bar (blocks/preorder-status.liquid).
 *
 * Two jobs, both of which exist because a product page outlives the boundary it
 * was rendered against - left open in a tab overnight, restored from the
 * bfcache, or served from a CDN cache:
 *
 *   1. count down to the close, days and hours until the last day and then
 *      hours and minutes, so the figure never claims precision it does not have;
 *   2. at zero, move the tracker on to "Being made", say "Ordering closed",
 *      and disable add to cart.
 *      Without that the page keeps offering a button that the redemption-lock
 *      validation refuses at checkout - a dead end with no explanation on it.
 *
 * It never does the reverse. Nothing here re-enables a button the server
 * disabled: the server is the authority on whether the window is open, and a
 * visitor whose clock runs fast would otherwise add a line that checkout then
 * refuses. Crossing from "opens soon" into "open" needs a reload, on purpose.
 */

const TIME_ZONE = 'America/New_York';
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;

/**
 * The shop's own clock, which is what both gates read. Eastern is hard-coded
 * here rather than taken from Liquid because the window is defined in Eastern -
 * see docs/preorders.md - and this way the countdown is right for a visitor in
 * any timezone, not just one browsing from the same place as the shop.
 */
const EASTERN = new Intl.DateTimeFormat('en-US', {
  timeZone: TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/**
 * Eastern's wall clock at `instant`, re-read as though it were UTC.
 *
 * @param {number} instant - Epoch milliseconds.
 * @returns {number} Epoch milliseconds of that wall-clock reading in UTC.
 */
function easternWallClockAsUtc(instant) {
  /** @type {Record<string, string>} */
  const parts = {};
  for (const { type, value } of EASTERN.formatToParts(new Date(instant))) {
    parts[type] = value;
  }

  return Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
}

/**
 * The instant midnight Eastern falls on a given day.
 *
 * A bare `YYYY-MM-DD` is not an instant until you know which offset was in
 * force that day, and the answer changes twice a year. `Intl` can only report
 * the offset for an instant you already hold, so this is the usual two-pass
 * fixed point: guess the instant as if the wall clock were UTC, ask New York
 * what its clock actually read there, and correct by the gap. One pass is
 * enough except when the correction carries the guess across a DST transition,
 * which is exactly what the second pass catches.
 *
 * `Intl` rather than a hard-coded -05:00/-04:00 table: the US has changed its
 * DST rules before, and the browser's copy of tzdata is maintained where a
 * table in this file would not be.
 *
 * @param {string} day - `YYYY-MM-DD`.
 * @param {number} [dayOffset] - Days to add first. `Date.UTC` normalises the
 *   overflow, so +1 on the 31st is the 1st of the next month.
 * @returns {number} Epoch milliseconds, or `NaN` if `day` is not a date.
 */
export function easternMidnight(day, dayOffset = 0) {
  const [year, month, date] = String(day).split('-').map(Number);
  if (!year || !month || !date) return NaN;

  const target = Date.UTC(year, month - 1, date + dayOffset);
  let instant = target;
  for (let pass = 0; pass < 2; pass++) {
    instant = target + (instant - easternWallClockAsUtc(instant));
  }

  return instant;
}

/**
 * Days and hours until the last day, then hours and minutes.
 *
 * "0d 7h" would be both uglier and less useful than "7h 12m" at the point it
 * starts to matter, which is the point the merchant asked for the switch.
 *
 * @param {number} remaining - Milliseconds.
 * @returns {string}
 */
function format(remaining) {
  const hours = Math.floor(remaining / HOUR);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;

  const minutes = Math.floor((remaining % HOUR) / MINUTE);
  if (hours > 0) return `${hours}h ${minutes}m`;

  // Under an hour, "0h 12m" reads as broken. Round up so it never shows 0m
  // while the window is still open.
  return `${Math.max(1, minutes)}m`;
}

/**
 * @typedef {object} PreorderCountdownRefs
 * @property {HTMLElement[]} segment - The three tracker steps, in order.
 * @property {HTMLElement} [note] - The ordering-date line in the card's head.
 * @property {HTMLElement} [countdown] - Empty span the live figure goes into.
 */

/**
 * @extends {Component<PreorderCountdownRefs>}
 */
class PreorderCountdown extends Component {
  requiredRefs = ['segment'];

  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;

  /** @type {number} Epoch ms, or NaN when there is nothing to count towards. */
  #closesAt = NaN;

  #expired = false;

  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    // A backgrounded tab throttles timers to once a minute at best and stops
    // them altogether when the device sleeps, so the clock is re-read on wake
    // rather than trusted to have kept ticking. `pageshow` covers the bfcache,
    // where the whole page comes back with its old DOM intact.
    document.addEventListener('visibilitychange', this.#tick, { signal });
    window.addEventListener('pageshow', this.#tick, { signal });

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /**
   * A variant switch re-renders the section and morphs this element, which can
   * replace the boundary underneath us. Re-read it rather than keep counting to
   * the old one.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  #start() {
    this.#stop();
    this.#expired = false;

    const closesOn = this.dataset.closesOn;
    // Midnight the morning *after* the last day: the window includes its end
    // date, and that same instant is when the shop's own date rolls over and
    // the validation function starts refusing the line. The two flip together.
    this.#closesAt = closesOn ? easternMidnight(closesOn, 1) : NaN;
    if (Number.isNaN(this.#closesAt)) return;

    this.#tick();
  }

  #stop() {
    if (this.#timer != null) clearTimeout(this.#timer);
    this.#timer = null;
  }

  #tick = () => {
    if (this.#expired || Number.isNaN(this.#closesAt)) return;

    const remaining = this.#closesAt - Date.now();
    if (remaining <= 0) {
      this.#close();
      return;
    }

    const { countdown } = this.refs;
    if (countdown) countdown.textContent = ` — ${format(remaining)} left`;

    // A chained timeout aligned to the next whole minute, not setInterval: the
    // display is never finer than a minute, and re-aligning on every tick means
    // a throttled or slept tab resyncs instead of accumulating drift.
    this.#schedule(remaining % MINUTE || MINUTE);
  };

  /** @param {number} delay - Milliseconds. */
  #schedule(delay) {
    this.#stop();
    this.#timer = setTimeout(this.#tick, Math.min(Math.max(delay, 250), MINUTE));
  }

  #close() {
    this.#expired = true;
    this.#stop();

    // A shut window moves the tracker to Being made, the step after Taking
    // orders - there is no Closed step; the close is a date on the card. A
    // merchant who has already set the stage is at or past that point, so the
    // tracker is never dragged backwards.
    if (Number(this.dataset.active ?? 0) < 1) {
      this.dataset.active = '1';
      for (const [index, segment] of this.refs.segment.entries()) {
        segment.dataset.state = index < 1 ? 'done' : index === 1 ? 'current' : 'todo';
      }
    }

    const { countdown, note } = this.refs;
    if (countdown) countdown.textContent = '';
    // The server renders the closed wording with the date already in it, so a
    // tab left open past the close reads exactly as a fresh load would.
    if (note) note.textContent = this.dataset.closedNote || 'Ordering closed';

    this.#disableOrdering();
  }

  /**
   * The buy buttons live outside this block, so they are found by query rather
   * than by ref. Both product-page shapes are covered - the form's button and
   * the sticky bar's - because missing either leaves one live route to a cart
   * line that checkout then refuses.
   */
  #disableOrdering() {
    const label = this.dataset.closedLabel || 'Preorder closed';

    const buttons = document.querySelectorAll(
      'add-to-cart-component button, .sticky-add-to-cart__button'
    );

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = true;
      relabel(button, label);
    }
  }
}

/**
 * Rewrite a buy button's label, whichever of the two shapes it is.
 *
 * The sticky bar wraps its label in `.add-to-cart-text__content`; the product
 * form's button (snippets/add-to-cart-button.liquid) holds it as a bare text
 * node between the cart icon and the checkmark-burst span. Walking to the first
 * non-empty text node handles that without adding a wrapper to a snippet three
 * other surfaces render.
 *
 * @param {HTMLButtonElement} button
 * @param {string} text
 */
function relabel(button, text) {
  const wrapped = button.querySelector('.add-to-cart-text__content > span:first-child');
  if (wrapped) {
    wrapped.textContent = text;
    return;
  }

  for (const node of button.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      node.textContent = text;
      return;
    }
  }
}

if (!customElements.get('preorder-countdown')) {
  customElements.define('preorder-countdown', PreorderCountdown);
}
